import {
  gelGetSupportedPlatforms, gelListConnectors, gelCreateConnector, gelTestConnector,
  listDpgRules, toggleDpgRule, getMyEntitlements,
  listIntentTemplates,
  listIntentTemplateVersions, createIntentTemplateDraft,
  submitIntentTemplateVersion, bindAgentToIntentTemplateVersion,
  getUsers, getGroups, getAssignments, createAssignment, removeAssignment,
  claimOwnership, attestOwnership,
  gelDiscoverCapabilities, gelUpdateSkillMappings, gelMaterializePolicies,
  manualAutonomyOverride,
  getAgentSpiffe,
  getSpiffeCapability,
  gatewayListKeysByAgent,
  listAgentIntentBindingsEnriched,
  listAgentIntentBindings,
  rebindAgentIntentBinding,
  getPolicySkillMaterialized,
  getAgentIntentBindingCoverage,
  detectAgentIntentBindingDrift,
  gelGetRegistrationByAgent,
  getExecutionBackend, getAgentConnectorAssignments, getAgentSkills,
  getLlmProviders, generateEval, runEval,
  listBuildAnswers,
  addSkill, reviewSkill,
  previewPhaseGate, gelAdvanceToDecide, gelAdvanceToAct,
  getEffectiveRules, getLiveTrustSnapshot,
  gelGetExecutionReadiness,
} from './api';
import { trustLevelFor } from '../data/protectionLimits';
import { isExteletProtectionSkill } from '../constants/skillTypes';
import { typeLabel } from '../components/v2/frameworkCatalog';
import { classifySvidStatus } from '../data/svidStatus';

/**
 * What Prove7 Protect is allowed to call, and the shape it needs the answers in.
 *
 * <h3>A connection is chosen, never conjured</h3>
 *
 * The console's registration wizard will not continue until a Platform
 * Connector exists for the framework, and sends the operator off to configure
 * one. Protect used to create one silently, which is a second way to make a
 * platform-level object from a tenant-facing screen — the exact divergence that
 * ends with two surfaces disagreeing about what a customer has.
 *
 * So Protect shows what this caller can actually use: the connections Prove7
 * shares into their tenant, and the ones their tenant owns. Adding their own
 * goes through the same `POST /gel/connectors` the console posts to, and is
 * proved with the same health check before anything is bound to it. Binding an
 * agent to a connection nobody has ever called is how a registration succeeds
 * and every run after it fails.
 *
 * <h3>Boundaries are rules that already exist</h3>
 *
 * "What must never leave" is not a new vocabulary invented for this screen. It
 * is the tenant's data classification rules — listed, and switched on. A rule
 * authored here would be a rule the Data Protection surface does not know
 * about, and the boundary would mean two different things in two places.
 */

const ACTIVE = 'ACTIVE';

/** The platform type as a person says it. Derived, so it cannot drift. */
export function frameworkLabel(platformType) {
  const t = String(platformType || '');
  if (!t) return '';
  /* The catalog is the platform's own naming — the same rows the registration
     wizard's framework picker is built from — so OPENAI reads "OpenAI" and
     VERTEX_AI reads "Google Vertex AI" on both screens. This used to title-case
     the enum here instead, which gave Protect a second, worse spelling of every
     vendor's name. */
  const known = typeLabel(t);
  if (known && known !== t) return known;
  /* A platform type the catalog has not been taught yet. Title-casing it is
     still better than putting a raw enum in front of a tenant. */
  return t.toLowerCase().split('_').filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const rows = (res) => {
  if (Array.isArray(res)) return res;
  const d = res?.data;
  return Array.isArray(d) ? d : [];
};

/** The response body, whether the client wrapped it in `data` or not. */
const body = (res) => (res && res.data !== undefined ? res.data : res);

/**
 * The platform's refusal, when it arrives as a value instead of a throw.
 *
 * <p>`apiFetch` catches its own errors and returns
 * `{ ok: false, error, status }`; it does not propagate. A call written as
 * `try { await x(); return ok; } catch { return fail; }` therefore reports
 * success for every refusal the server sends. Observed live: the review step
 * POSTed, the platform answered 403 "role 'Protect' lacks required
 * permissions", and the step showed nothing — and a refused phase advance
 * would have been recorded as advanced.</p>
 *
 * @returns the message when the call was refused, or null when it was not
 */
const refusal = (res) => (res && res.ok === false
  ? (res.error || `The platform refused this${res.status ? ` (${res.status})` : ''}.`)
  : null);

/**
 * Every framework the backend supports, and the live connections behind each.
 *
 * `provided` are connections shared into this tenant — Prove7's. `own` are the
 * tenant's. The flag is the API's own `isOwner`, flattened by the client; it is
 * not inferred from a name.
 */
export async function readConnections() {
  try {
    const [platformsRes, connectorsRes] = await Promise.all([
      gelGetSupportedPlatforms(),
      gelListConnectors(),
    ]);
    const types = rows(platformsRes).map((t) => (typeof t === 'string' ? t : t?.platformType))
      .filter(Boolean);
    const live = rows(connectorsRes).filter((c) => c?.status === ACTIVE);

    const frameworks = types.map((platformType) => {
      const mine = live.filter((c) => c.platformType === platformType);
      return {
        platformType,
        label: frameworkLabel(platformType),
        /* `__isOwner === false` means this connection belongs to another tenant
           and was shared in — which, for a customer, is Prove7's. */
        provided: mine.filter((c) => c.__isOwner === false),
        own: mine.filter((c) => c.__isOwner !== false),
      };
    });
    return { frameworks, error: null };
  } catch (e) {
    /* Said out loud. Rendering a failed read as "no connections configured"
       tells somebody their estate is empty, which is a different claim. */
    return { frameworks: [], error: e?.message || 'Could not read your connections.' };
  }
}

/**
 * Connect the customer's own framework, through the platform's connector API,
 * and prove it before its id is handed back to be bound.
 */
export async function connectOwn({
  platformType, name, endpointUrl, authMethod = 'API_KEY', apiKey, description,
}) {
  const key = String(apiKey || '').trim();
  const res = await gelCreateConnector({
    platformType,
    name,
    endpointUrl,
    authMethod,
    /* An empty box is not a credential. Sending `{apiKey: ''}` would store a
       blank secret and fail at the first call with a confusing 401. */
    authConfig: key ? { apiKey: key } : {},
    description: description || `${name} — ${frameworkLabel(platformType)} agent framework`,
  });
  if (res && res.ok === false) {
    throw new Error(res.error || res.message || 'The connection was refused.');
  }
  const created = (res && 'data' in res) ? res.data : res;
  const id = created?.id;
  if (!id) throw new Error('The connection was created but came back without an id.');

  const health = await gelTestConnector(id).catch((e) => ({ data: {
    status: 'UNHEALTHY', message: e?.message || 'The health check could not be run.',
  } }));
  const h = (health && 'data' in health) ? health.data : health;
  return { id, name, health: h?.status || 'UNKNOWN', message: h?.message || null,
    latencyMs: h?.latencyMs ?? null };
}

/**
 * The tenant's data classification rules, grouped by what they protect, with
 * whether that category is already switched on.
 */
export async function readBoundaries() {
  try {
    const all = rows(await listDpgRules());
    const order = [];
    const byCategory = new Map();
    all.forEach((r) => {
      const category = r?.category || 'CUSTOM';
      if (!byCategory.has(category)) { byCategory.set(category, []); order.push(category); }
      byCategory.get(category).push(r);
    });
    const categories = order.map((category) => {
      const group = byCategory.get(category);
      return {
        category,
        label: group[0]?.name || category,
        ruleIds: group.map((r) => r.id),
        /* On when every rule behind it is on — a category half-enforced is not
           a boundary, and saying it is would be the lie this product cannot
           afford. */
        on: group.every((r) => r.enabled === true),
        blocking: group.some((r) => r.action === 'BLOCK'),
      };
    });
    return { categories, error: null };
  } catch (e) {
    return { categories: [], error: e?.message || 'Could not read your boundary rules.' };
  }
}

/** Switch on exactly the rules chosen, and say which would not switch. */
export async function applyBoundaries(ruleIds) {
  const ids = Array.isArray(ruleIds) ? ruleIds.filter(Boolean) : [];
  const applied = [];
  const failed = [];
  /* One at a time, in the order chosen: the result says which rules are on,
     and a parallel burst would make a partial failure unreadable. */
  for (const id of ids) {
    try {
      const res = await toggleDpgRule(id, true);
      if (res && res.ok === false) failed.push(id); else applied.push(id);
    } catch {
      failed.push(id);
    }
  }
  return { applied, failed };
}

/**
 * The allowance behind a Prove7-provided connection.
 *
 * It is a quota on the customer's own plan, so it is read rather than written
 * into the copy. When the plan carries no LLM quota this returns null and the
 * screen says nothing about limits — a word like "free" that nobody can check
 * is worse than no claim at all.
 */
export async function readProvidedAllowance() {
  try {
    const res = await getMyEntitlements();
    const body = (res && 'data' in res) ? res.data : res;
    const quotas = Array.isArray(body?.quotas) ? body.quotas : [];
    const llm = quotas.find((q) => q?.key === 'LLM_COST_USD');
    if (!llm || llm.hard == null) return null;
    return { limit: llm.hard, unit: llm.unit || null, period: llm.period || null };
  } catch {
    return null;
  }
}

/* ── the intent an agent is bound to ───────────────────────────────────── */

/**
 * The intents this tenant can actually bind to.
 *
 * A binding is refused unless the template version is APPROVED, and the
 * Execution-Ready gate checks that a binding exists. A template is bindable
 * when approval has pointed it at an active version — approving sets
 * `activeVersionId` and moves the template to ACTIVE — so that pointer is the
 * question asked here. Offering anything else would offer a bind the platform
 * will reject.
 */
export async function readBindableIntents() {
  try {
    const all = rows(await listIntentTemplates());
    const intents = all
      .filter((t) => t?.activeVersionId && String(t.state).toUpperCase() === 'ACTIVE')
      .map((t) => ({
        templateId: t.id,
        versionId: t.activeVersionId,
        label: t.displayName || t.name,
        description: t.description || null,
        riskTier: t.riskTier || null,
      }));
    return { intents, error: null };
  } catch (e) {
    /* Said out loud. A caller without the permission to list templates has not
       got an estate with no intents in it. */
    return { intents: [], error: e?.message || 'Could not read your intents.' };
  }
}

/** Bind the agent to an approved version, or report the platform's refusal. */
export async function bindIntent(agentId, templateVersionId) {
  const res = await bindAgentToIntentTemplateVersion(agentId, templateVersionId);
  if (res && res.ok === false) {
    throw new Error(res.error || res.message || 'The binding was refused.');
  }
  const body = (res && 'data' in res) ? res.data : res;
  return { bindingId: body?.id || null, templateVersionId };
}

/**
 * Draft a new intent from what somebody described, and put it in the queue.
 *
 * It does not come back bindable and must never be reported as though it did.
 * A new version needs an authored Rego and an approver who is not its author —
 * enforced in the service and again by a database CHECK — so the most this can
 * do is create the draft, submit it for review, and say who has to act next.
 */
export async function proposeIntent({ name, displayName, description, riskTier,
  category, orgId, tenantId }) {
  const created = await createIntentTemplateDraft({
    scopeLevel: 'TENANT',
    orgId,
    tenantId,
    name,
    displayName: displayName || name,
    description,
    riskTier: riskTier || 'MEDIUM',
    category: category || null,
  });
  if (created && created.ok === false) {
    throw new Error(created.error || created.message || 'The intent could not be drafted.');
  }
  const template = (created && 'data' in created) ? created.data : created;
  const templateId = template?.id;
  if (!templateId) throw new Error('The intent was drafted but came back without an id.');

  /* Create records the template and its first DRAFT version, but answers with
     the template alone — so the version to submit is read back. */
  const versions = rows(await listIntentTemplateVersions(templateId).catch(() => null));
  const draft = versions.find((v) => String(v?.state).toUpperCase() === 'DRAFT') || versions[0];
  if (!draft?.id) {
    return { templateId, versionId: null, submitted: false, bindable: false,
      problem: 'The draft was created but its first version could not be read back.' };
  }

  const sent = await submitIntentTemplateVersion(draft.id).catch((e) => ({
    ok: false, error: e?.message }));
  const submitted = !(sent && sent.ok === false);
  return {
    templateId,
    versionId: draft.id,
    submitted,
    /* Never true here. Approval is somebody else's act. */
    bindable: false,
    problem: submitted ? null : (sent.error || 'It was drafted but not submitted for review.'),
  };
}

/* ── who may run it ────────────────────────────────────────────────────── */

/** The access level that names an approver rather than an operator. */
export const APPROVE = 'approve';

/**
 * The levels the model defines, in the order it lists them.
 *
 * `approve` is last and apart on purpose: under segregation of duties it is an
 * approval DUTY, not access. The readiness gate counts only the others, so an
 * agent whose single grant is `approve` has nobody able to run it.
 */
export const ACCESS_LEVELS = [
  { value: 'view', label: 'View — can see it and what it did' },
  { value: 'execute', label: 'Execute — can run it' },
  { value: 'manage', label: 'Manage — can run and change it' },
  { value: 'admin', label: 'Admin — full control of this agent' },
  { value: APPROVE, label: 'Approve — decides its human-approval gates, cannot run it' },
];

/** The people and groups this tenant can grant to. */
/**
 * Whether a policy skill is actually enforcing anything yet.
 *
 * Creating a policy skill makes a shell. What gives it teeth is the
 * configuration pipeline — source selection, extraction, review, binding,
 * activation — which turns documents, patterns or framework controls into
 * enforcement rules attributed to that skill.
 *
 * This is not a cosmetic distinction: a policy with no rules in force attributes
 * nothing and refuses nothing, and the Decide review has nothing to read. So the
 * step that creates it has to know the difference, and the only honest test is
 * whether rules exist and are switched on.
 *
 * CORRECTION (2026-11-03). This used to say an unconfigured policy scores 4 of 7
 * and that configuring it produced 7 of 7. That was a misreading of a live 57%.
 * The 4 of 7 is the config-completeness fallback EvalService scores when no judge
 * runs at all, and the 7 of 7 run was judged by the tenant's connected Anthropic
 * connector — the variable was the judge, not the configuration. See
 * docs/DESIGN_EVAL_JUDGE_ONE_OWNER.md. The step still earns its place on the
 * ground above; it was never load-bearing on the eval score.
 *
 * A rule is counted only when it is explicitly enabled. Unknown is not treated
 * as on: letting a policy through on an unreadable flag is how the shell gets
 * past, and the shell is the thing this exists to catch.
 *
 * @returns {{ok:boolean, total:number, enabled:number}}
 */
export async function readPolicyEnforcement(skillId) {
  const none = { ok: false, total: 0, enabled: 0 };
  if (!skillId) return none;

  let res;
  try {
    res = await getPolicySkillMaterialized(skillId);
  } catch (_) {
    return none;
  }
  /* apiFetch resolves a refusal rather than throwing, and this read needs Tenant Admin — so a
     refusal is a value, not an exception, and must not read as "enforces nothing": that would
     hold someone at a step they had already finished, with nothing saying why. Checked FIRST,
     because the unwrap below cannot tell a refusal from an empty list once it has flattened it. */
  if (res && res.ok === false) return none;

  /* The client wraps some bodies in `data` and returns others raw. Observed live: this read
     answered 200 with the rules in `data`, the raw value failed an Array check, and the step
     reported a policy it could not see as one it could not check. */
  const list = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : null);
  if (list === null) return none;

  return {
    ok: true,
    total: list.length,
    enabled: list.filter((r) => r && r.enabled === true).length,
  };
}

/**
 * Re-resolve an agent's intent against the skills it actually has.
 *
 * A binding resolves its matchers at bind time and freezes the result. Nothing
 * re-resolves it afterwards, and that is deliberate — an approved intent must
 * never widen because somebody added a capability. The cost is that an intent
 * bound before the agent had skills froze an empty set, and every later call
 * naming a skill is refused by a binding that authorizes none.
 *
 * This is the person's deliberate "resolve it again", which is what the
 * platform's rebind is for: the binding row and its approved template version
 * are kept, and only what it resolves to is recomputed. Their original choice
 * of intent is applied, not discarded.
 *
 * Only bindings that are not doing their job are touched — STALE, or ACTIVE
 * while resolving nothing. One already resolving skills is left alone, because
 * re-resolving it is precisely how authority would widen unasked. ORPHANED is
 * counted and skipped: its template version was purged, so there is nothing to
 * resolve against and attempting it returns an error about a missing version
 * that tells the reader nothing.
 *
 * @returns {{ok:boolean, rebound:number, failed:number, orphaned:number}}
 */
export async function refreshIntentBinding(agentId) {
  const none = { ok: false, rebound: 0, failed: 0, orphaned: 0 };
  if (!agentId) return none;

  let rows;
  try {
    rows = await listAgentIntentBindings(agentId);
  } catch (_) {
    return none;
  }
  /* apiFetch resolves a refusal rather than throwing, so a body that is not a list is how a
     failed read arrives. Reading it as "no bindings" would report success for a read that
     never happened. */
  if (!Array.isArray(rows)) return none;

  const stateOf = (b) => String(b?.state || '').toUpperCase();
  const orphaned = rows.filter((b) => stateOf(b) === 'ORPHANED').length;
  const stale = rows.filter((b) => {
    if (!b || !b.id || stateOf(b) === 'ORPHANED') return false;
    if (stateOf(b) === 'STALE') return true;
    return !(Array.isArray(b.resolvedSkillIds) && b.resolvedSkillIds.length > 0);
  });

  let rebound = 0;
  let failed = 0;
  for (const b of stale) {
    try {
      /* eslint-disable-next-line no-await-in-loop */
      const res = await rebindAgentIntentBinding(agentId, b.id);
      /* A refusal resolves here too, so a body carrying an error is a failure, not a rebind. */
      if (res && res.error) failed += 1; else rebound += 1;
    } catch (_) {
      failed += 1;
    }
  }
  return { ok: failed === 0, rebound, failed, orphaned };
}

export async function readTeam({ orgId, tenantId } = {}) {
  try {
    const [usersRes, groupsRes] = await Promise.all([
      getUsers({ orgId, tenantId }),
      getGroups({ orgId, tenantId }),
    ]);
    return {
      users: rows(usersRes).map((u) => ({
        id: u.id, label: u.name || u.email || u.id, email: u.email || null,
      })),
      groups: rows(groupsRes).map((g) => ({ id: g.id, label: g.name || g.id })),
      error: null,
    };
  } catch (e) {
    /* A caller who cannot read the directory has not got an empty one. */
    return { users: [], groups: [], error: e?.message || 'Could not read your team.' };
  }
}

/**
 * What is granted on this agent, split the way the readiness gate splits it.
 *
 * `satisfied` answers the gate's own question — at least one grant that is not
 * an approval duty — so the surface and the gate cannot disagree.
 */
export async function readAccess(agentId) {
  try {
    const all = rows(await getAssignments(agentId));
    const approvers = all.filter((a) => String(a.accessLevel).toLowerCase() === APPROVE);
    const runtime = all.filter((a) => String(a.accessLevel).toLowerCase() !== APPROVE);
    return { runtime, approvers, satisfied: runtime.length > 0, error: null };
  } catch (e) {
    return { runtime: [], approvers: [], satisfied: false,
      error: e?.message || 'Could not read who has access.' };
  }
}

/** Grant one person or one group a level on this agent. */
export async function grantAccess(agentId, { userId, groupId, accessLevel }) {
  if (!userId && !groupId) {
    throw new Error('A grant has to name a person or a group.');
  }
  if (!ACCESS_LEVELS.some((l) => l.value === accessLevel)) {
    throw new Error(`"${accessLevel}" is not an access level this platform has.`);
  }
  /* Exactly one subject per grant: the backend takes userId or groupId, and
     sending both would leave which one was granted to the server to guess. */
  const payload = userId ? { userId, accessLevel } : { groupId, accessLevel };
  const res = await createAssignment(agentId, payload);
  if (res && res.ok === false) {
    throw new Error(res.error || res.message || 'The grant was refused.');
  }
  const body = (res && 'data' in res) ? res.data : res;
  return { assignmentId: body?.id || null, accessLevel };
}

/**
 * Take a grant back.
 *
 * <h3>Why it belongs on this step</h3>
 *
 * The step could hand out authority and never take it back, so a grant made by
 * mistake — the wrong person, or the wrong level — could only be undone by
 * leaving for the full platform. Access is the one step here that gives
 * somebody power over an agent, which makes it the one that most needs an undo.
 *
 * <h3>Why offering it is safe</h3>
 *
 * Revoking asks for `assignment.manage`, exactly what granting asks for, and
 * the handler refuses a cross-tenant agent before it touches anything. So
 * anybody who could make the grant on this screen can remove it; this is not a
 * button that will be refused.
 *
 * <h3>Why nothing is subtracted locally</h3>
 *
 * The endpoint answers 204 with no body. Who has access afterwards is a read,
 * because the readiness gate counts those same rows and two opinions about who
 * can run an agent is one too many.
 */
export async function revokeAccess(agentId, assignmentId) {
  if (!assignmentId) throw new Error('There is nothing to remove — which grant?');
  let res;
  try {
    res = await removeAssignment(agentId, assignmentId);
  } catch (e) {
    throw new Error(e?.status === 403
      ? 'You do not have permission to remove access to this agent.'
      : (e?.message || 'The removal was refused.'));
  }
  if (res && res.ok === false) {
    throw new Error(res.error || res.message || 'The removal was refused.');
  }
  return { removed: true };
}

/**
 * The ownership states a person can actually do something about from here.
 *
 * <h3>Why this is a list and not a judgement each row makes</h3>
 *
 * `OwnershipConsentService.claimOwnership` accepts exactly one state — it reads
 * the agent's live record and refuses with 409 `not_claimable` for anything that
 * is not DISCOVERED_UNOWNED (`:86-90`). So an action offered on any other state
 * is a button whose only possible outcome is an error.
 *
 * The three that are deliberately absent, and what each would need:
 *
 * - NOMINATED — a nudge to the person who has not accepted yet. There is no
 *   endpoint; OwnershipController exposes claim, attest and invite-owner, and
 *   nothing that reminds.
 * - ORPHANED — the owner no longer exists, which is the state that most needs a
 *   re-assignment, and the one with no path. `OwnershipRecordService.applyTransition`
 *   could carry it, but it has no HTTP surface.
 * - RETIRED — terminal by design. Claim refuses it, and should.
 *
 * Each of those still SAYS its state on the row, because the fact is true and
 * worth knowing. What it no longer does is invite a click that cannot complete.
 */
export const CLAIMABLE = 'DISCOVERED_UNOWNED';

/**
 * Name the accountable owner for an agent nobody answers for.
 *
 * <h3>Why "myself" still sends a principal</h3>
 *
 * `claimOwnership` refuses a null owner with 400 `owner_required` (`:75-77`):
 * the ceremony names a person, and "me" is a person. So the caller's own id is
 * sent, and then attested in the same breath — a nomination of oneself that was
 * left un-accepted would park the agent in NOMINATED, waiting on the person who
 * is already here.
 *
 * Nominating somebody else stops at NOMINATED on purpose. Accountability is
 * accepted, never assigned: the named owner accepts through their own tasks
 * list, which is the only place `attest` will take it from (it is owner-only,
 * backend-enforced).
 */
export async function nominateOwner(agentId, intent, selfPrincipalId) {
  const self = intent && intent.ownerMode === 'self';
  const ownerPrincipalId = self ? selfPrincipalId : (intent && intent.ownerPrincipalId) || null;

  if (!agentId) return { ok: false, error: 'No agent to nominate an owner for.' };
  if (!ownerPrincipalId) {
    return {
      ok: false,
      error: self
        ? 'We could not tell who you are signed in as, so there is nobody to record.'
        : 'Choose the person who will answer for this agent.',
    };
  }

  try {
    await claimOwnership(agentId, ownerPrincipalId);
    if (self) {
      /* Accepted here because the person accepting is the person asking. A
         failure to attest leaves a real NOMINATED record, which is a weaker
         outcome than intended but not a wrong one — so it is reported without
         undoing the nomination. */
      try {
        await attestOwnership(agentId);
        return { ok: true, state: 'ACTIVE', error: null };
      } catch (e) {
        return { ok: true, state: 'NOMINATED', error: e?.message || null };
      }
    }
    return { ok: true, state: 'NOMINATED', error: null };
  } catch (e) {
    return { ok: false, error: e?.message || 'The nomination was refused.' };
  }
}

/* ── What the agent can do, and what it may do ───────────────────────────── */

/**
 * Ask the platform what this external agent exposes.
 *
 * <h3>Why an empty answer is not a failure</h3>
 *
 * Not every platform supports introspection. The backend says so in its own
 * words and returns an empty list rather than an error, so an agent on a
 * platform that cannot be asked is still protectable — its capabilities get
 * named by hand instead. Reporting that as a failure would stop a journey with
 * nothing wrong with it.
 */
export async function readCapabilities(registrationId) {
  try {
    const found = body(await gelDiscoverCapabilities(registrationId));
    const list = Array.isArray(found?.capabilities) ? found.capabilities : [];
    return {
      capabilities: list.map((c) => ({
        name: c.name,
        description: c.description || '',
        riskLevel: c.riskLevel || null,
        suggestedTags: Array.isArray(c.suggestedTags) ? c.suggestedTags : [],
      })),
      message: found?.message || null,
      error: null,
    };
  } catch (e) {
    return {
      capabilities: [],
      message: null,
      error: e?.message || 'Could not ask this agent what it can do.',
    };
  }
}

/**
 * Record which of those capabilities this agent is allowed to use.
 *
 * <h3>Why everything else waits on this step</h3>
 *
 * Protection binds to capabilities — one proxy skill per mapping.
 * `materializeProxySkills` returns an empty list when there are no mappings,
 * and `materializePolicyConfigurations` refuses outright without at least one
 * proxy skill. So an agent with nothing mapped cannot have a single rule
 * enforced on it, however many limits are set afterwards.
 *
 * The platform materializes the proxies inside this same call, so this is one
 * request and not two.
 */
export async function mapCapabilities(registrationId, chosen) {
  const mappings = (Array.isArray(chosen) ? chosen : [])
    .filter((c) => c && c.name)
    .map((c) => ({
      name: c.name,
      description: c.description || '',
      ...(c.category ? { category: c.category } : {}),
    }));

  if (!mappings.length) {
    return { ok: false, mapped: 0, error: 'Choose at least one thing this agent may do.' };
  }
  try {
    await gelUpdateSkillMappings(registrationId, mappings);
    return { ok: true, mapped: mappings.length, error: null };
  } catch (e) {
    return { ok: false, mapped: 0, error: e?.message || 'Could not record what this agent may do.' };
  }
}



/**
 * What this agent's operator was asked, and what they answered.
 *
 * <p>Unwrapped here rather than at the screen: the response-shape rule in the architecture
 * principles is one wire shape, one decoder, and a per-screen `data.items ?? data.content ?? data`
 * ladder is the thing it names. An empty list is the honest answer for an agent that predates the
 * record, and the trail falls back to the platform's own facts for it.</p>
 */
export async function readBuildAnswers(agentId) {
  if (!agentId) return [];
  try {
    return rows(body(await listBuildAnswers(agentId)));
  } catch {
    return [];
  }
}

/**
 * The bar a skill must clear to leave Build, read from the gate that enforces it.
 *
 * <p>`PhaseGateService.BUILD_TO_DECIDE_THRESHOLD = 0.70f`. It was tightened from
 * "any non-zero score" on 2026-05-25 so the gate agrees with the eval suite's own
 * pass mark. Stated here once; nothing in the surface types a number of its own.</p>
 */
export const EVAL_BAR = 0.70;

/** The authored protection policy's skill type — the single object the bridge
 *  attributes an ExteLet's materialized rules to. The bridge's own
 *  `external_policy_proxy` rows are a different thing and it writes those itself. */
const SKILL_TYPE_POLICY = 'policy_skill';

/**
 * Where each of this agent's skills stands against that bar.
 *
 * <h3>Why the wizard has to care</h3>
 *
 * <p>`isSkillEvalReady:756` requires, per skill, a completed EvalSuite linked to
 * THAT skill and `evalScore >= 0.70`. Another skill's eval does not count. With
 * none, `at_least_one_eval_ready` fails and the agent cannot reach Decide — and
 * nothing in Protect used to run one, which is where the walk stopped.</p>
 */
export async function readEvalState(agentId) {
  if (!agentId) {
    return { skills: [], providers: [], anyReady: false, canGenerate: false, error: null };
  }
  const settle = async (fn, fallback) => {
    try { return await fn(); } catch { return fallback; }
  };

  const [providers, all] = await Promise.all([
    settle(async () => rows(body(await getLlmProviders(agentId))), []),
    settle(async () => rows(body(await getAgentSkills(agentId))), []),
  ]);

  /* An ExteLet is measured on its protection units, not on its tools.
     Materializing creates one `external_policy_proxy` per mapped capability —
     the "GEL: …" skills, which carry pending_review and are what the Decide
     gate's review is about. Its authored `policy_skill` is measured too, and
     has to be: PhaseGateService.promoteEvalReadyBuildSkills:348 only lifts a
     skill to decide once it has a completed eval at or above the bar, and an
     unmeasured policy skill therefore sits in build where nothing can review
     it. Both are what isExteletProtectionSkill selects. The `connector_skill`
     rows beside them are the tools the agent may call; measuring those asks
     whether the vendor's endpoint behaves, which is a different question from
     whether this agent's protection holds. The first version of this read
     measured the tools. */
  const skills = all
    .filter(isExteletProtectionSkill)
    .map((sk) => ({
      id: sk.id,
      name: sk.name,
      score: typeof sk.evalScore === 'number' ? sk.evalScore : null,
      ready: typeof sk.evalScore === 'number' && sk.evalScore >= EVAL_BAR,
    }));

  return {
    skills,
    providers: providers.map((p) => ({ id: p.id, name: p.name, modelId: p.modelId })),
    anyReady: skills.some((sk) => sk.ready),
    /* An empty provider list is a state with a consequence, not an absence:
       without one nothing can generate an eval, so the gate cannot be cleared
       from here at all. */
    canGenerate: providers.length > 0,
    error: null,
  };
}

/**
 * Generate an eval suite for one skill and run it.
 *
 * <h3>Why both halves, and in this order</h3>
 *
 * <p>The gate wants a COMPLETED suite and a passing score. Generation alone
 * leaves a suite nobody ran; a run needs a suite to run. `EvalService:746` is
 * what writes `skill.evalScore` when the run completes, so the score is read
 * back off the skill rather than inferred from the run's own response — the
 * skill is what the gate reads.</p>
 */
export async function runEvalForSkill(agentId, skillId, provider) {
  if (!agentId || !skillId) {
    return { ok: false, score: null, ready: false, error: 'No skill to evaluate.' };
  }
  if (!provider) {
    return { ok: false, score: null, ready: false,
      error: 'No model is configured for this tenant to generate an eval with.' };
  }
  /* A judge is identified by the connector it names, or by naming none. The platform's built-in
     judge has no connector and no model of its own, and this used to demand both — so the one
     judge always available was the one Protect could not use, and a tenant whose only usable
     provider was down had no way past this step at all. A judge that DOES name a connector still
     has to carry a model: that model is chosen by the one owner that knows which of a provider's
     models can hold a conversation, and measuring without it would hand a prompt to whatever the
     vendor defaults to. */
  const platformJudge = !provider.id;
  if (!platformJudge && !provider.modelId) {
    return { ok: false, score: null, ready: false,
      error: 'That model cannot be used to measure a skill. Choose another.' };
  }

  let generated;
  try {
    /* Nothing is sent for the built-in judge. The platform reads a missing connector id as its
       own judge and stamps its model, so naming either here would be a second place for them to
       be spelled — and the first to go stale. */
    const payload = platformJudge
      ? { skillId }
      : { skillId, llmProviderId: provider.id, llmModelId: provider.modelId };
    generated = await generateEval(agentId, payload);
  } catch (e) {
    return { ok: false, score: null, ready: false,
      error: e?.message || 'The platform would not generate an eval for this skill.' };
  }
  /* A refusal arrives as a VALUE here, not a throw. `EvalController.generateEvals` maps every
     RuntimeException out of generation to 409 with {"error": "<the reason>"}, and apiFetch
     returns a 409 rather than throwing so a caller can handle a real conflict. Driven live:
     forty seconds of "Measuring…" and then "The platform returned no eval suite to run" —
     while the reason sat unread in `.error`. That generic sentence belongs only to a 2xx that
     genuinely carried no suite. */
  const refusedGenerate = refusal(generated);
  if (refusedGenerate) {
    return { ok: false, score: null, ready: false, error: refusedGenerate };
  }
  const suiteId = body(generated)?.id;
  if (!suiteId) {
    return { ok: false, score: null, ready: false,
      error: 'The platform returned no eval suite to run.' };
  }

  let ran;
  try {
    ran = await runEval(agentId, suiteId);
  } catch (e) {
    return { ok: false, score: null, ready: false,
      error: e?.message || 'The eval was generated but would not run.' };
  }
  /* The same hole one call later: this was awaited for its effect and never read, so a run the
     platform refused — the judge it named could not be reached, which this build now fails
     outright rather than scoring — was reported as a measurement that happened. */
  const refusedRun = refusal(ran);
  if (refusedRun) {
    return { ok: false, score: null, ready: false, error: refusedRun };
  }

  /* Read the score where the gate reads it. */
  const after = await readEvalState(agentId);
  const sk = after.skills.find((x) => x.id === skillId);
  return { ok: true, score: sk?.score ?? null, ready: Boolean(sk?.ready), error: null };
}


/**
 * Put this agent's limits in force.
 *
 * <h3>One answer about autonomy, two writes</h3>
 *
 * "How much may it do on its own" is two objects here: the enforcement cap the
 * gateway checks on every protected call, and the trust ceiling that satisfies
 * the Decide gate. Two vocabularies, at different heights — which is why the
 * pairing lives in `protectionLimits.AUTONOMY_CHOICES`, asserted against both
 * backend ladders, rather than being decided in this function.
 *
 * <h3>Rules first, ceiling second</h3>
 *
 * If the ceiling fails afterwards the agent is protected but will not pass its
 * gate: a partial state somebody can see and retry. The other order would raise
 * a ceiling on an agent with no rules behind it, which is the failure that
 * matters.
 */
/**
 * The agent's authored protection policy, if it has one.
 *
 * <h3>Why "sole" is the whole contract</h3>
 *
 * <p>`ExternalAgentPolicyBridge.resolveSolePolicySkillId:253` attributes every
 * rule the bridge materializes to the agent's one and only `policy_skill`, and
 * returns null when the agent has none OR several — two is as useless to it as
 * none. With null, the reconcile pass at `:308-320` is skipped, and that pass
 * is what retires rules the tenant has stopped asking for; a limit switched off
 * in the wizard kept enforcing with nothing able to clear it.</p>
 *
 * <p>So this reports the count, not just the first match. A step that silently
 * picked one of two would show a tenant an owner the backend does not use.</p>
 *
 * <p>The bridge's own `external_policy_proxy` rows are NOT this. They are one
 * per mapped capability and the bridge writes them itself; the authored policy
 * is the single object protection is attributed to.</p>
 */
/**
 * A named phase gate, as the platform states it.
 *
 * <h3>Named, not inferred</h3>
 *
 * <p>`previewPhaseGate` defaults to the gate the agent is STANDING at, so a
 * step that wants the Build gate has to say so — otherwise an agent already in
 * Decide hands back the Decide gate and the step renders a foreign gate's
 * checks under its own heading. `PhaseGateService.previewGate:144-147` takes
 * the from-phase for exactly this reason and also returns `actionable`, which
 * says whether the advance is live or merely previewed.</p>
 *
 * <h3>Each check's sentence is the platform's</h3>
 *
 * <p>Every gate producer emits {check, passed, message} and `message` is the
 * sentence. A label map on this side would be a second source for it, correct
 * only for the keys whoever wrote it had in mind — which is the defect the
 * Decide tab shipped with. `GateChecks` renders the message for the readiness
 * gate already; this keeps one convention.</p>
 *
 * <h3>A gate that could not be read never passes</h3>
 *
 * <p>`allPassed` is false on any failure. Reporting a clear gate because a
 * request fell over is the worst thing this could claim.</p>
 */
/** The ladder, so "past this gate" is a comparison and not a guess. */
const PHASE_ORDER = Object.freeze(['build', 'decide', 'act']);

export async function readPhaseGate(agentId, fromPhase) {
  const empty = { checks: [], allPassed: false, actionable: false,
    currentPhase: null, alreadyPast: false, error: null };
  if (!agentId || !fromPhase) return empty;
  let data;
  try {
    data = body(await previewPhaseGate(agentId, fromPhase));
  } catch (e) {
    return { ...empty, error: e?.message || 'Could not read this agent\u2019s gate.' };
  }
  const checks = (Array.isArray(data?.checks) ? data.checks : []).map((c) => ({
    id: c.check,
    passed: !!c.passed,
    detail: c.message || null,
  }));
  /* `actionable: false` covers two different situations — the agent has not
     reached this gate, and the agent is already past it — and a step that
     cannot tell them apart tells a resumed agent to clear a step it already
     cleared, with no way onward. `currentPhase` separates them
     (PhaseGateService:160). */
  const currentPhase = typeof data?.currentPhase === 'string'
    ? data.currentPhase.toLowerCase() : null;
  const at = PHASE_ORDER.indexOf(currentPhase);
  const of = PHASE_ORDER.indexOf(String(fromPhase).toLowerCase());
  return {
    checks,
    allPassed: data?.allPassed === true,
    actionable: data?.actionable !== false,
    currentPhase,
    alreadyPast: at >= 0 && of >= 0 && at > of,
    error: null,
  };
}

/**
 * Advance the agent out of the named phase.
 *
 * <h3>Why the GEL lifecycle and not the native advance</h3>
 *
 * <p>`PhaseGateService.advancePhase` refuses an external agent outright —
 * "External agents must be advanced through the GEL lifecycle API" — because
 * the GEL-specific gates would be bypassed and the registration's status and
 * the agent's phase would desynchronise. The lifecycle calls enforce the same
 * gate `readPhaseGate` previewed, and each also promotes the skills that
 * qualify: `advanceToBuild:169` lifts eval-ready skills to decide, and
 * `advanceToAct:288` lifts reviewed ones to act.</p>
 *
 * <h3>Why the from-phase decides the call</h3>
 *
 * <p>Two endpoints, one per step, and nothing here may guess: an advance sent
 * to the wrong one is rejected on a phase mismatch, which reads to a tenant as
 * a gate failing for no reason. An unknown phase is refused rather than
 * defaulted.</p>
 */
export async function advanceAgentPhase(registrationId, fromPhase) {
  if (!registrationId) {
    return { ok: false, error: 'This agent has no registration to advance.' };
  }
  const call = fromPhase === 'build' ? gelAdvanceToDecide
    : fromPhase === 'decide' ? gelAdvanceToAct
      : null;
  if (!call) {
    return { ok: false, error: `There is no advance out of the ${fromPhase} phase.` };
  }
  try {
    const res = await call(registrationId);
    const refused = refusal(res);
    if (refused) return { ok: false, error: refused };
    return { ok: true, error: null };
  } catch (e) {
    return { ok: false, error: e?.message || 'The platform would not advance this agent.' };
  }
}

/** The decisions the platform accepts; anything else is refused before the call. */
const REVIEW_DECISIONS = Object.freeze(['autonomous', 'human_approval', 'blocked']);

/** Autonomous needs this much, per SkillReviewService:56-63. */
const AUTONOMOUS_BAR = 0.90;

/** The review status a blocked decision leaves behind (SkillReviewService:89). */
const REVIEW_STATUS_BLOCKED = 'blocked';

/**
 * The statuses that mean somebody actually decided.
 *
 * <p>`reviewStatus` is NOT a review flag: it defaults to `pending_review` on
 * the entity itself (`Skill.java:100`), so every skill carries it from the
 * moment it exists. Treating a non-null value as a decision reported skills
 * nobody had looked at as reviewed — observed live on an agent with four
 * proxies at `pending_review` and zero SkillReview rows — which hid their
 * decision buttons and let the walk continue into an Act advance the backend
 * refuses. A decision is what `SkillReviewService:80-89` writes, and nothing
 * else.</p>
 */
const REVIEW_DECIDED = Object.freeze(['promoted', 'reviewed', REVIEW_STATUS_BLOCKED]);

/**
 * The protection skills a review can actually be made on.
 *
 * <h3>Why phase, not just type</h3>
 *
 * <p>`SkillReviewService:50` refuses a review while the skill is still in
 * build, and a skill only leaves build once it has a completed eval at or above
 * the bar (`promoteEvalReadyBuildSkills:348`, run by the Build → Decide
 * advance). Offering a review on a build-phase skill would put the platform's
 * refusal where the explanation belongs, so those are counted as waiting
 * instead and the step can say what they are waiting for.</p>
 *
 * <h3>Why the autonomy bar is read here</h3>
 *
 * <p>Autonomous is refused below 0.90. A step that offered it anyway would
 * trade an explanation for an error message, so each skill carries whether it
 * is eligible and the step offers accordingly.</p>
 */
export async function readReviewableSkills(agentId) {
  const empty = { skills: [], waiting: 0, anyReviewed: false, error: null };
  if (!agentId) return empty;
  let all;
  try {
    all = rows(body(await getAgentSkills(agentId)));
  } catch (e) {
    return { ...empty, error: e?.message || 'Could not read this agent\u2019s skills.' };
  }

  const protection = all.filter(isExteletProtectionSkill);
  const reviewable = protection.filter(
    (sk) => sk?.skillPhase === 'decide' || sk?.skillPhase === 'act');
  const skills = reviewable.map((sk) => ({
    id: sk.id,
    name: sk.name,
    score: typeof sk.evalScore === 'number' ? sk.evalScore : null,
    reviewed: REVIEW_DECIDED.includes(sk.reviewStatus),
    /* reviewStatus is promoted | reviewed | blocked (SkillReviewService:84-90).
       Blocked is a review, and it is the one the gate does not accept. */
    blocked: sk.reviewStatus === REVIEW_STATUS_BLOCKED,
    canBeAutonomous: typeof sk.evalScore === 'number' && sk.evalScore >= AUTONOMOUS_BAR,
  }));
  return {
    skills,
    waiting: protection.length - reviewable.length,
    anyReviewed: skills.some((sk) => sk.reviewed),
    /* The predicate the Decide gate actually applies: at least one review that
       is NOT blocked (hasNonBlockedReviewedSkill, enforced at
       ExternalAgentLifecycleService:249). Counting "reviewed" instead would let
       a walk that held every skill back carry on to an advance it cannot pass. */
    anyCleared: skills.some((sk) => sk.reviewed && !sk.blocked),
    error: null,
  };
}

/**
 * Record a review decision against one protection skill.
 *
 * <p>The decision is what the Decide gate reads: `hasNonBlockedReviewedSkill`
 * needs at least one that is not blocked before the agent may advance to Act,
 * and an autonomous decision is checked against the score on both sides —
 * here by the platform (`SkillReviewService:56-63`) and again at the gate
 * (`autonomousSkillsBelowThreshold`).</p>
 */
export async function reviewProtectionSkill(agentId, skillId, decision, justification) {
  if (!agentId || !skillId) {
    return { ok: false, error: 'No skill to review.' };
  }
  if (!REVIEW_DECISIONS.includes(decision)) {
    return { ok: false, error: `"${decision}" is not a decision this platform records.` };
  }
  try {
    const res = await reviewSkill(agentId, skillId,
      { decision, justification: justification || '' });
    const refused = refusal(res);
    if (refused) return { ok: false, error: refused };
    return { ok: true, error: null };
  } catch (e) {
    return { ok: false, error: e?.message || 'The platform would not record this review.' };
  }
}


export async function readPolicySkill(agentId) {
  if (!agentId) {
    return { skill: null, count: 0, ambiguous: false, error: null };
  }
  let all;
  try {
    all = rows(body(await getAgentSkills(agentId)));
  } catch (e) {
    return { skill: null, count: 0, ambiguous: false,
      error: e?.message || 'Could not read this agent\u2019s skills.' };
  }
  const authored = all.filter((sk) => sk?.skillType === SKILL_TYPE_POLICY);
  const first = authored[0] || null;
  return {
    /* The sole one, or null. Named `skill` because that is what "this agent's policy" means
       when there is exactly one, which is the ordinary case. */
    skill: authored.length === 1 && first
      ? { id: first.id, name: first.name, description: first.description || null }
      : null,
    /* All of them. Two is a state the Build gate accepts, so the step that attributes limits
       has to be able to offer the choice rather than refuse the agent. */
    policies: authored.map((sk) => ({
      id: sk.id, name: sk.name, description: sk.description || null,
    })),
    count: authored.length,
    ambiguous: authored.length > 1,
    error: null,
  };
}

/**
 * Create the agent's protection policy.
 *
 * <h3>The sole-owner check happens before the write</h3>
 *
 * <p>A second `policy_skill` does not fail loudly — it leaves
 * `resolveSolePolicySkillId` returning null exactly as zero does, so the agent
 * silently loses the reconcile pass again. Nothing downstream would report
 * that, which is why this refuses up front rather than letting the platform
 * accept a write that breaks attribution.</p>
 *
 * <p>Created through the same `POST /agents/{id}/skills` the console's own
 * skill creation posts to, so one write path makes the row and the
 * `PolicySkillConfiguration` the Policies list is keyed on
 * (`AgentService:1481-1494` creates it inside the same transaction).</p>
 */
export async function createPolicySkill(agentId, { name, description } = {}) {
  if (!agentId) {
    return { ok: false, skill: null, error: 'No agent to add a protection policy to.' };
  }
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (!trimmed) {
    return { ok: false, skill: null, error: 'Give this protection policy a name.' };
  }

  const existing = await readPolicySkill(agentId);
  if (existing.error) {
    return { ok: false, skill: null, error: existing.error };
  }
  if (existing.count > 0) {
    return { ok: false, skill: existing.skill,
      error: 'This agent already has a protection policy. Its limits are attributed '
        + 'to that one, and a second would leave the platform with no single owner '
        + 'for them.' };
  }

  try {
    const res = await addSkill(agentId, {
      name: trimmed,
      description: typeof description === 'string' ? description : '',
      skillType: SKILL_TYPE_POLICY,
      policyConfig: null,
    });
    /* Checked rather than inferred: this used to rely on a refused call
       carrying no `data`, so the missing id stood in for the refusal and the
       platform's own sentence was thrown away. */
    const refused = refusal(res);
    if (refused) return { ok: false, skill: null, error: refused };
    const created = body(res);
    return {
      ok: true,
      skill: created ? { id: created.id, name: created.name,
        description: created.description || null } : null,
      error: null,
    };
  } catch (e) {
    return { ok: false, skill: null,
      error: e?.message || 'Could not create this agent\u2019s protection policy.' };
  }
}


export async function applyProtectionLimits(registrationId, agentId, limits, policySkillId) {
  const governancePolicies = {
    dataClassification: limits.dataClassification,
    maxAutonomyLevel: limits.maxAutonomyLevel,
    dpgScanLevel: limits.dpgScanLevel,
    requiredApprovals: Number(limits.requiredApprovals) || 0,
    rateLimitPerMinute: Number(limits.rateLimitPerMinute) || 60,
  };

  let rules = 0;
  try {
    /* Which policy these rules belong to. Omitted, the platform resolves the agent's only one
       and nothing changes for an agent that has one. Named, it is checked against this agent
       before anything is written — an attribution the platform refuses must surface as a
       refusal here, not as limits reported in force. */
    const res = await gelMaterializePolicies(registrationId, governancePolicies, policySkillId);
    const refused = refusal(res);
    if (refused) return { ok: false, rules: 0, ceiling: null, error: refused };
    rules = body(res)?.materializedCount ?? 0;
  } catch (e) {
    return { ok: false, rules: 0, ceiling: null, error: e?.message || 'Could not put these limits in force.' };
  }

  if (!agentId) {
    return { ok: true, rules, ceiling: null, error: null };
  }

  const ceiling = trustLevelFor(limits.maxAutonomyLevel);
  try {
    await manualAutonomyOverride(agentId, ceiling, 'Ceiling set while protecting this agent');
    return { ok: true, rules, ceiling, error: null };
  } catch (e) {
    /* The rules are in force; only the ceiling is missing. Reporting a failure
       would send somebody back to re-enter limits that already landed — and
       handing back the platform's bare words ("403 Access denied") would do the
       same thing more quietly, because nothing in them says half of this
       worked. So the sentence leads with what landed, and carries the
       platform's reason rather than replacing it. */
    const why = e?.message ? ` ${e.message}` : '';
    return {
      ok: true,
      rules,
      ceiling: null,
      error: `This agent's limits are in force, but its autonomy ceiling was not set, `
        + `so it will not pass its gate yet.${why}`,
    };
  }
}


/**
 * What this ExteLet can actually do, for the Promote step.
 *
 * <h3>The gap this closes</h3>
 *
 * <p>An ExteLet built through Protect finished with no skills at all: nothing in
 * the wizard ever gave it any. Its intent binding then froze nothing, and the
 * Intent step said so plainly — "0 skills frozen at bind time. It allows no
 * skill, so every call that declares this intent is refused." That is a refusal
 * for having no capability, not for any protection decision, and it is why an
 * end-to-end run never reached a meaningful answer.</p>
 *
 * <p>An ExteLet gets its capability in Promote's Build phase: it runs on a live
 * connector, and that connector's advertised actions are added as governed
 * skills. The picker that does it already exists and is reused as it stands —
 * this read is only what the step needs to show it.</p>
 *
 * <h3>Four answers to one question</h3>
 *
 * <p>Which backend it runs on, which connectors are attached, and how many
 * governed skills it holds are one question — can this agent do anything yet.
 * They are read together so the wizard does not walk a second path to an answer
 * the v2 Build surface already has, and each is isolated so one read failing
 * reports itself rather than all three.</p>
 */
export async function readConnectorSkills(agentId) {
  if (!agentId) {
    return { backend: null, assignments: [], skillCount: 0, skills: [], error: null };
  }

  /* Isolated deliberately. An agent whose assignments cannot be read still has
     a skill count worth showing, and vice versa. */
  const settle = async (fn, fallback) => {
    try { return await fn(); } catch { return fallback; }
  };

  const [backend, assignments, skills] = await Promise.all([
    /* `executionBackend` is what the response carries (AgentController:786);
       `backend` is only the PUT's request key. */
    settle(async () => body(await getExecutionBackend(agentId))?.executionBackend ?? null,
      null),
    settle(async () => rows(body(await getAgentConnectorAssignments(agentId))), []),
    settle(async () => rows(body(await getAgentSkills(agentId))), []),
  ]);

  /* Only connector-bound skills count here. A policy skill is not a capability
     the agent can be asked to exercise, and counting it would report an agent
     as able to act when it cannot. */
  const governed = skills.filter((sk) => sk?.skillType === 'connector_skill');

  return {
    backend,
    assignments,
    skills: governed,
    skillCount: governed.length,
    error: null,
  };
}

/**
 * The identity this agent was given, read back from the platform.
 *
 * <h3>Why the stage shows it instead of asserting it</h3>
 *
 * "Stamped it with a name of its own" is a claim. The SPIFFE name, the trust
 * domain it belongs to, and the AI-system claims carried in the SVID extension
 * are the evidence for it, and they are what the platform's own Identity tab
 * shows. A stage whose definition is "SPIFFE/SVID issued; cryptographic name
 * stamped" should put that on screen, not summarise it in a sentence.
 *
 * <h3>Honest about the certificate</h3>
 *
 * The name is minted at registration; an X.509 SVID is a separate act, so
 * `svidActive` is false on a freshly registered agent. That is a NORMAL state,
 * not a failure.
 *
 * It is deliberately not said here HOW one comes to exist. An earlier version
 * of this comment claimed the SVID arrives when the workload first presents
 * itself, that claim was copied into the user-facing step, and it is only one
 * of two cases: with the SPIRE Workload API unwired nothing is issued by
 * presenting, and a tenant told otherwise waits for something that cannot
 * happen. The capability is read and reported instead — see `canMint`.
 */
export async function readIdentity(agentId) {
  if (!agentId) return { error: 'No agent to read an identity for.' };
  /* Both reads, together. The capability says whether ANYTHING can mint a
     certificate; without it the only honest answer about a missing SVID is
     "we do not know", and a button that cannot work is worse than none. It is
     allowed to fail on its own — a tenant who cannot read the platform's SPIRE
     capability can still be shown the name their agent carries. */
  const [detail, capability] = await Promise.all([
    getAgentSpiffe(agentId),
    getSpiffeCapability().catch(() => null),
  ]).catch((e) => [e, null]);

  if (detail instanceof Error || detail?.status === 403 || detail?.ok === false) {
    return { error: detail?.status === 403
      ? 'You do not have permission to read this agent\u2019s identity.'
      : (detail?.message || detail?.error
         || 'Could not read this agent\u2019s identity just now.') };
  }

  try {
    const d = body(detail);
    const m = d?.aiSystemMetadata || {};
    const cap = capability ? body(capability) : null;
    return {
      spiffeId: d?.spiffeId || null,
      trustDomain: d?.trustDomain || null,
      svidActive: Boolean(d?.svidActive),
      svidSerial: d?.svidSerial || null,
      svidExpiresAt: d?.svidExpiresAt || null,
      /* The platform's own four buckets, from data/svidStatus.js. Reading
         `svidActive` alone reported an expired certificate as active. */
      status: classifySvidStatus({
        svidActive: d?.svidActive, svidExpiresAt: d?.svidExpiresAt,
      }),
      /* Whether the SPIRE Workload API is wired. False when the capability
         could not be read: not known is not the same as yes. */
      canMint: Boolean(cap?.workloadApiConnected),
      /* The SPIRE registration entry the platform builds for this agent on
         every read of this endpoint (SpiffeController:218). It is what an
         operator pastes into the SPIRE server they run, and it is the only
         thing a surface can hand over where nothing here can mint. This read
         discarded it, so the step that needed it had nothing to offer. Its
         presence is the signal; the file itself is fetched from the endpoint
         that serves it with a filename. */
      registrationEntryYaml: d?.registrationEntryYaml || null,
      /* Named for what they are on the wire. The platform writes these into
         the SVID extension; renaming them here would make two vocabularies
         for one set of claims. All eight the identity panel shows. */
      claims: {
        kind: m.aiSystemKind || null,
        autonomy: m.aiSystemAutonomy || null,
        riskTier: m.aiSystemRiskTier || null,
        trustScore: typeof m.aiSystemTrustScore === 'number' ? m.aiSystemTrustScore : null,
        policyCount: typeof m.aiSystemPolicyCount === 'number' ? m.aiSystemPolicyCount : null,
        frameworks: Array.isArray(m.aiSystemFrameworks) ? m.aiSystemFrameworks : [],
        platformVersion: m.aiSystemPlatformVersion || null,
        issuedAt: m.aiSystemIssuedAt || null,
      },
      error: null,
    };
  } catch (e) {
    return { error: e?.message || 'Could not read this agent\u2019s identity just now.' };
  }
}

/**
 * What the agent authenticates as.
 *
 * <h3>Why Identity needs this</h3>
 *
 * The plaintext keys are shown once, at the mint, and never again — only
 * bcrypt hashes are kept and nothing can return the originals. Protect said
 * nothing about them after that, so the stage whose subject is what the agent
 * presents when it calls carried no statement that it had anything to present.
 * The platform's own Identity tab lists them beside the SPIFFE name.
 *
 * <h3>What is readable, and what is not</h3>
 *
 * Which kinds exist, whether each is live, the prefix that identifies it in a
 * log, and what it has done. Never the plaintext — it does not exist to be
 * read, and a shape that carried a field for it would invite somebody to try.
 */
export async function readCredentials(agentId) {
  if (!agentId) return { keys: [], error: null };
  try {
    const keyRows = rows(await gatewayListKeysByAgent(agentId));
    return {
      keys: keyRows.map((k) => ({
        id: k.id,
        kind: KEY_KIND[k.keyType] || k.keyType || 'Key',
        status: k.status || null,
        /* ACTIVE is the only state that authenticates a call. SUSPENDED and
           REVOKED both refuse, and the difference between them matters to
           whoever has to fix it, so the status is carried as well. */
        live: k.status === 'ACTIVE',
        prefix: k.keyPrefix || null,
        lastUsedAt: k.lastUsedAt || null,
        invocations: typeof k.totalInvocations === 'number' ? k.totalInvocations : 0,
      })),
      error: null,
    };
  } catch (e) {
    /* Said out loud. An empty list means this agent has no credentials, which
       is a different and much more alarming thing than not being allowed to
       look. */
    return { keys: [], error: e?.status === 403
      ? 'You do not have permission to read this agent\u2019s credentials.'
      : (e?.message || 'Could not read this agent\u2019s credentials just now.') };
  }
}

/** The platform's own words for each key type. */
const KEY_KIND = { GATEWAY: 'Gateway', SDK: 'SDK', MCP: 'MCP' };

/**
 * The journey stage mapped to each check the Execution-Ready gate evaluates.
 *
 * <h3>Why a map and not a derivation</h3>
 *
 * This used to work the answer out client-side from four separate reads —
 * SPIFFE row, intent bindings, assignments, executionReadyAt. That was a second
 * code path to an answer the platform already owns, and it could disagree with
 * the gate that actually refuses the agent: it covered four of these nine, so
 * an agent could read as finished here and still be turned away for an unarmed
 * kill switch or a probe that never ran.
 *
 * `GET /{id}/execution-readiness` returns the same checklist
 * `markExecutionReady` enforces — its own words: "the displayed gate == the
 * enforced gate". So the checklist is read, and all that is added is which
 * stage each check belongs to, which is presentation rather than judgement.
 */
const CHECK_STAGE = {
  registration_exists: 'discovery',
  owner_consent_and_accountability: 'discovery',
  identity_valid: 'identity',
  intent_bound: 'intent',
  assignment_present: 'access',
  /* Promote is where an ExteLet's rules actually come from. Its Build phase is
     where the live connector is chosen and that connector's actions are added
     as governed skills; the rules in force follow from those. This was mapped
     to Intent on the reasoning that materializing an approved version's Rego
     is part of binding it — which is true of the Rego and false of the
     surface, and the cost was concrete: an agent that was already bound
     resumed at Intent, where the only thing on offer was the binding it
     already had, while the step that would clear the check sat two stages
     further on. */
  governance_materialized: 'promote',
  phase_act: 'promote',
  green_probe: 'enforce',
  kill_switch_ready: 'enforce',
};

/** Journey order, for picking the first stage that is not clear. */
const STAGE_ORDER = ['discovery', 'identity', 'intent', 'access', 'promote', 'enforce'];

/**
 * Where a half-finished build picks up — read from the gate the platform
 * enforces.
 *
 * <h3>Read, never remembered and never re-derived</h3>
 *
 * Before the commit a build is a draft and a saved cursor is the whole truth.
 * After it, the agent is real, and what is true about it is whatever the
 * Execution-Ready gate says when asked. That is also what makes this right when
 * the walk was abandoned in another browser, or when somebody else bound its
 * intent while it sat there.
 *
 * <h3>Why a failed read stops everything</h3>
 *
 * A stage is unmet only when the gate says so. A call that did not come back
 * says nothing — and treating silence as "unmet" would land somebody on Intent
 * because a request timed out, where they would bind a second intent over the
 * one already there.
 */
export async function readJourneyState(agentId) {
  if (!agentId) {
    return { resumeAt: null, done: {}, checks: [], complete: false,
      registrationId: null, error: null };
  }

  /* registrationId travels with every shape, including the failures. The
     Promote step is called with it, and a caller that has to go and fetch it
     again would be a second read of something this function already has. */
  const fail = (message) => ({
    resumeAt: null, done: {}, checks: [], complete: false,
    registrationId: null, error: message,
  });

  let registrationId;
  try {
    registrationId = body(await gelGetRegistrationByAgent(agentId))?.id;
  } catch (e) {
    return fail(e?.status === 404
      ? 'This agent has no registration, so there is no readiness gate to read.'
      : 'Could not read this agent\u2019s registration.');
  }
  if (!registrationId) {
    return fail('This agent has no registration, so there is no readiness gate to read.');
  }

  let checks;
  try {
    /* One call. The payload is {checks: [...]}; the fallback is for a server
       that returns the list bare. */
    const payload = body(await gelGetExecutionReadiness(registrationId));
    checks = rows(payload?.checks || payload);
  } catch (e) {
    return fail(e?.status === 403
      ? 'You do not have permission to read this agent\u2019s readiness.'
      : 'Could not read where this agent had got to. Nothing is assumed from a read '
        + 'that did not come back.');
  }

  /* A stage is clear only when EVERY check mapped to it has passed. Intent has
     two; treating either alone as the stage would call an agent bound whose
     policy was never materialized. */
  /* The wire calls it `check`; ExternalAgentLifecycleService:477 puts the name
     there. Reading `name` matched nothing, so no check could mark a stage
     unmet and every agent read as fully ready — including one with no intent
     bound at all. Both spellings are accepted so this cannot break again from
     the far side. */
  const nameOf = (c) => c.check || c.name || null;

  const done = {};
  STAGE_ORDER.forEach((stage) => { done[stage] = true; });
  checks.forEach((c) => {
    const stage = CHECK_STAGE[nameOf(c)];
    if (!stage) return;            // a check we have no stage for changes nothing
    if (!c.passed) done[stage] = false;
  });

  const resumeAt = STAGE_ORDER.find((stage) => !done[stage]) || null;

  return {
    resumeAt,
    done,
    /* What the Promote step acts with. */
    registrationId,
    /* Every check, unmapped, so nothing is lost by grouping them. */
    checks: checks.map((c) => ({
      name: nameOf(c), passed: Boolean(c.passed), message: c.message || null,
    })),
    complete: resumeAt === null,
    error: null,
  };
}

/**
 * What is already bound to this agent, and whether it works.
 *
 * <h3>Why the step has to ask</h3>
 *
 * Protect's Intent step only ever offered to bind. It never asked what was
 * bound already, so an agent picked back up mid-journey was offered a fresh
 * binding as though it had none — and a second binding over the top of the
 * first is a different agent from the one somebody thought they were
 * finishing.
 *
 * <h3>Three states, not two</h3>
 *
 * ACTIVE is a live binding. STALE means the template moved underneath it.
 * ORPHANED means what it pointed at is gone. Reading only "is there a row"
 * reports the last two as done, which is how an agent ends up bound to
 * something that no longer decides anything.
 *
 * <h3>Coverage is a separate question</h3>
 *
 * A binding authorizes some of the agent's callable tools and not others. One
 * that authorizes none is bound and useless: every call refuses at runtime,
 * while the stage reads as cleared. That is worth knowing here rather than on
 * the first call, so the coverage is read per binding — and a coverage read
 * that fails says nothing about the binding, which still reports.
 */
export async function readIntentBindings(agentId) {
  if (!agentId) return { bound: false, bindings: [], error: null };
  let raw;
  try {
    raw = rows(await listAgentIntentBindingsEnriched(agentId));
  } catch (e) {
    /* Null, not false. Not allowed to look is not the same as nothing bound,
       and offering to bind on the strength of a refused read would stack a
       second binding on an agent that already has one. */
    return { bound: null, bindings: [], error: e?.status === 403
      ? 'You do not have permission to read what is bound to this agent.'
      : (e?.message || 'Could not read what is bound to this agent.') };
  }

  const bindings = await Promise.all(raw.map(async (b) => {
    let coverage = null;
    try {
      const c = body(await getAgentIntentBindingCoverage(agentId, b.id)) || {};
      coverage = {
        authorized: (c.authorized || []).length,
        unauthorized: (c.unauthorized || []).length,
        unevaluated: (c.unevaluated || []).length,
      };
    } catch {
      /* Left null. A coverage call that did not come back is not a binding
         that authorizes nothing, and showing it as such would be alarming and
         wrong. */
    }
    const name = b.templateName || b.intentName || 'Intent';

    /* What the binding froze. The runtime builds its allow-list from exactly
       these ids minus the narrowed ones, as literal patterns — so an empty
       result is an allow-list that matches no skill, and
       IntentAuthorizationService turns a non-match into skillOutsideIntent,
       which is a block. Bound, enforcing and refusing everything are all true
       at once in that case, which is why it is counted rather than implied. */
    const frozen = list(b.resolvedSkillIds);
    const narrowed = list(b.narrowingExcludedSkillIds);
    const excluded = new Set(narrowed);
    const authorizesNoSkill = frozen.filter((id) => !excluded.has(id)).length === 0;

    return {
      id: b.id,
      label: b.versionNumber ? `${name} \u00b7 v${b.versionNumber}` : name,
      state: b.state || null,
      /* Only ACTIVE decides anything. */
      live: b.state === 'ACTIVE',
      staleReason: b.staleReason || null,
      lastDriftCheckAt: b.lastDriftCheckAt || null,
      frozenSkills: frozen.length,
      narrowedSkills: narrowed.length,
      authorizesNoSkill,
      /* Per-request enforcement is on only once the version was compiled into
         an agent-scoped policy rule; without one the gate has nothing to
         evaluate on a call that declares no intent. */
      enforcing: Boolean(b.boundPolicyRuleId),
      /* A binding pins one version deliberately — nothing widens it silently.
         Moving to a newer approved version is an operator's act, so the fact
         that one exists has to be said rather than discovered. */
      newerVersion: b.hasNewerApprovedVersion
        ? { number: b.latestApprovedVersionNumber ?? null,
            id: b.latestApprovedVersionId || null }
        : null,
      coverage,
      authorizesNothing: Boolean(coverage && coverage.authorized === 0),
    };
  }));

  return { bound: bindings.length > 0, bindings, error: null };
}

/**
 * Where an ExteLet's registration stands, now.
 *
 * <h3>Why it is asked again at all</h3>
 *
 * PENDING_VERIFICATION is not a resting state: the platform flips it to VERIFIED
 * on its own when the agent's first protected call succeeds, with nothing
 * pressed. A surface that read it once at commit and never looked again goes on
 * reporting "the first call verifies it" after the first call already has.
 *
 * <h3>Why the status is returned bare</h3>
 *
 * The caller decides what to do with a flip — the console toasts and refreshes
 * its card, Protect changes a sentence. What they must agree on is the question
 * and the vocabulary, which is why both read the same endpoint and both judge it
 * with data/registrationStatus rather than with a string literal of their own.
 */
export async function readRegistrationStatus(agentId) {
  if (!agentId) return { status: null, error: null };
  try {
    const row = body(await gelGetRegistrationByAgent(agentId));
    return { status: row?.registrationStatus || null, error: null };
  } catch (e) {
    /* A read that did not come back is not a state change. The caller keeps
       whatever it had rather than reporting a status nobody read. */
    return { status: null, error: e?.status === 403
      ? 'You do not have permission to read this registration.'
      : (e?.message || 'Could not read this registration just now.') };
  }
}

/**
 * Ask the platform to re-check one binding for drift, now.
 *
 * <h3>Why on demand</h3>
 *
 * Drift is found by a scheduled sweep over bindings whose last check has aged
 * past a cutoff. Between sweeps a binding reads ACTIVE while the skills under
 * it have already moved, so reporting only the stored state reports a state
 * nobody has re-checked. The endpoint is read-tier — it asks for
 * `agent.read`, the same permission as listing the bindings — so anybody who
 * can see this can ask for it.
 *
 * <h3>Why the new state is not read from the answer</h3>
 *
 * The handler returns `driftDetected`, `bindingId` and `agentId`, and nothing
 * else. Its own API description promises a `bindingState` it never puts on the
 * wire, so the post-check state comes from re-reading the bindings.
 */
export async function checkIntentDrift(agentId, bindingId) {
  if (!agentId || !bindingId) return { drifted: null, error: null };
  try {
    const out = body(await detectAgentIntentBindingDrift(agentId, bindingId)) || {};
    return { drifted: Boolean(out.driftDetected), error: null };
  } catch (e) {
    return { drifted: null, error: e?.status === 403
      ? 'You do not have permission to check this binding for drift.'
      : (e?.message || 'The drift check could not be run.') };
  }
}

/**
 * The one state a version can be bound in.
 *
 * `IntentBindingServiceImpl.bind` refuses anything that is not APPROVED with a
 * 409, and ACTIVE is not a version state at all — the CHECK constraint on
 * intent_template_versions.state allows DRAFT, IN_REVIEW, APPROVED and
 * DEPRECATED, while ACTIVE belongs to the TEMPLATE row. Offering an "ACTIVE
 * version" would offer a bind the platform cannot perform.
 */
const BINDABLE_VERSION_STATES = new Set(['APPROVED']);

/** A JsonNode column arrives as an array, or as null. */
const list = (v) => (Array.isArray(v) ? v : []);

/**
 * The three ways a version names the skills it allows.
 *
 * Each entry in `allowed_skill_matchers` is an object — {mode, value} — and
 * SkillMatcherResolver skips anything that is not one, or that is missing
 * either field. So this reads the same entries the platform will resolve at
 * bind time, and leaves out the ones it would ignore: showing a matcher that
 * resolves to nothing is promising an allowance that will not exist.
 */
const MATCHER_MODE = {
  EXPLICIT: 'explicit-skill',
  NAME: 'skill-name',
  GLOB: 'skill-glob',
};

/**
 * What a matcher is called on screen.
 *
 * A name and a glob say themselves. An explicit matcher carries a skill id,
 * and a raw UUID in front of somebody is not an answer — it is shortened the
 * way the rest of the platform shortens ids.
 */
function matcherLabel(mode, value) {
  if (mode === MATCHER_MODE.EXPLICIT) return `skill ${String(value).slice(0, 8)}`;
  return String(value);
}

function readMatchers(node) {
  return list(node)
    .filter((m) => m && typeof m === 'object' && !Array.isArray(m))
    .map((m) => ({ mode: m.mode || null, value: m.value || null }))
    .filter((m) => m.mode && m.value
      && Object.values(MATCHER_MODE).includes(m.mode))
    .map((m) => ({ ...m, label: matcherLabel(m.mode, m.value) }));
}

/**
 * The versions of one intent template, and what each one actually says.
 *
 * <h3>Why the version is a separate choice</h3>
 *
 * `readBindableIntents` exposes one version per template — the active one — so
 * there was nothing to choose and the picker was a flat wall of tiles. The
 * platform's own bind dialog asks for a template and THEN a version, because a
 * template is a name and a version is the thing that decides: its Rego, what
 * it allows, how far it lets an agent go.
 *
 * <h3>Two properties change whether binding it is wise</h3>
 *
 * `securityCritical` gives bound agents a hard banner and auto-blocks them at
 * the Act phase — its own field comment says so. `hasBrokenRefs` means it
 * points at skills that have since been deleted. Binding either looked exactly
 * like binding a clean one, which is the whole problem.
 */
export async function readIntentVersions(templateId) {
  if (!templateId) return { versions: [], error: null };
  try {
    const all = rows(await listIntentTemplateVersions(templateId));
    const versions = all
      .filter((v) => BINDABLE_VERSION_STATES.has(String(v.state || '').toUpperCase()))
      .map((v) => ({
        id: v.id,
        label: `v${v.versionNumber}`,
        versionNumber: v.versionNumber,
        state: v.state || null,
        /* How far this version lets an agent go on its own. */
        maxAutonomy: v.maxAutonomy || null,
        requiresApproval: Boolean(v.requiresApproval),
        /* A hard banner and an auto-block at Act, per the field's own
           contract. Worth seeing before binding, not after. */
        securityCritical: Boolean(v.securityCritical),
        securityCriticalReason: v.securityCriticalReason || null,
        /* Points at skills that have since been deleted. */
        hasBrokenRefs: Boolean(v.hasBrokenRefs),
        /* What the binding freezes: the matchers it resolves and the
           connectors it may reach. */
        allowedSkillMatchers: readMatchers(v.allowedSkillMatchers),
        allowedConnectors: list(v.allowedConnectors),
      }));
    return { versions, error: null };
  } catch (e) {
    /* Said out loud. Not being allowed to list versions is not a template
       with nothing bindable in it. */
    return { versions: [], error: e?.status === 403
      ? 'You do not have permission to read this intent\u2019s versions.'
      : (e?.message || 'Could not read this intent\u2019s versions.') };
  }
}


/**
 * How many rules are holding this agent, as the platform counts them.
 *
 * <h3>Why this is read and not counted here</h3>
 *
 * <p>`GET /api/v1/policy/effective-rules` already resolves the bindings for one
 * agent, tenant-scoped and gated on `policy.read`. Counting rows on this side
 * would be a second answer to "what holds this agent" — and the obvious
 * candidate, `/dpg/rules`, answers a different question entirely: those are the
 * tenant's data-classification rules, not this agent's enforcement bindings.</p>
 *
 * <h3>Why a failed read is not a zero</h3>
 *
 * <p>`apiFetch` RESOLVES a refusal rather than throwing, so a count read
 * straight off the response is `undefined` — and `undefined` rendered as a
 * number is `0`. On the one surface whose job is showing that the boundary is
 * live, that would tell somebody their agent is unprotected because a request
 * was refused. `null` means "could not tell"; only the platform's own number
 * means none.</p>
 */
export async function readAgentRuleCount(agentId) {
  if (!agentId) return { count: null, error: null };
  let res;
  try {
    res = await getEffectiveRules(agentId);
  } catch (e) {
    return { count: null, error: e?.message || 'Could not read what is holding this agent.' };
  }
  const refused = refusal(res);
  if (refused) return { count: null, error: refused };
  const data = body(res);
  const n = data?.bindingCount;
  return Number.isFinite(n)
    ? { count: n, error: null }
    : { count: null, error: null };
}


/**
 * The score this agent has EARNED by running inside the boundary.
 *
 * <h3>Two fields, kept apart on purpose</h3>
 *
 * <p>The live snapshot's `score` is documented as "0-100, or null if never
 * computed", and `totalEvaluations` is how many protected runs it was computed
 * from. The caller has to be able to tell "no score" from "a score with nothing
 * behind it" — collapsing them here would make a computed placeholder
 * indistinguishable from an earned number one layer up, which is the defect
 * this closes.</p>
 *
 * <p>A refused or failed read returns `runs: null`, never `0`: "I could not
 * tell" must not render as "it has never run".</p>
 */
export async function readRunTimeTrust(agentId) {
  const none = { score: null, tier: null, runs: null, error: null };
  if (!agentId) return none;
  let res;
  try {
    res = await getLiveTrustSnapshot(agentId);
  } catch (e) {
    return { ...none, error: e?.message || 'Could not read this agent\u2019s Trust Score.' };
  }
  const refused = refusal(res);
  if (refused) return { ...none, error: refused };
  const data = body(res);
  const score = Number(data?.score);
  const runs = Number(data?.totalEvaluations);
  return {
    score: Number.isFinite(score) && data?.score !== null ? score : null,
    tier: typeof data?.tier === 'string' && data.tier ? data.tier : null,
    runs: Number.isFinite(runs) ? runs : null,
    error: null,
  };
}
